import * as RadixAvatar from "@radix-ui/react-avatar";
import type { JSX } from "react";

import { avatarStyle } from "@/lib/avatar";
import { cn } from "@/lib/cn";
import { initials } from "@/lib/format";

export type AvatarChannel = "telegram" | "instagram";

export interface AvatarProps {
  name: string;
  src?: string | null | undefined;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | undefined;
  /** The 12px badge in the corner: where this person writes from. */
  channel?: AvatarChannel | null | undefined;
  className?: string | undefined;
}

const SIZE: Record<NonNullable<AvatarProps["size"]>, string> = {
  xs: "size-5 text-xs",
  sm: "size-6 text-xs",
  md: "size-8 text-xs",
  lg: "size-9 text-sm",
  xl: "size-10 text-sm",
};

function ChannelBadge({ channel }: { channel: AvatarChannel }): JSX.Element {
  return (
    <span
      className={cn(
        "absolute -right-0.5 -bottom-0.5 inline-flex size-3 items-center justify-center rounded-pill text-channel-ink outline-2 outline-surface",
        channel === "instagram" ? "bg-channel-instagram" : "bg-channel-telegram",
      )}
      aria-hidden="true"
    >
      <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="block">
        {channel === "instagram" ? (
          <>
            <rect width="20" height="20" x="2" y="2" rx="5" />
            <circle cx="12" cy="12" r="4" />
          </>
        ) : (
          <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
        )}
      </svg>
    </span>
  );
}

/** A person: photo when there is one, otherwise initials on the name's own colour. */
export function Avatar({ name, src, size = "md", channel, className }: AvatarProps): JSX.Element {
  const face = (
    <RadixAvatar.Root
      role="img"
      aria-label={name}
      style={avatarStyle(name)}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill font-semibold select-none",
        SIZE[size],
        channel === undefined || channel === null ? className : undefined,
      )}
    >
      {src === undefined || src === null || src === "" ? null : (
        <RadixAvatar.Image src={src} alt="" className="size-full object-cover" />
      )}
      <RadixAvatar.Fallback delayMs={0}>{initials(name)}</RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
  if (channel === undefined || channel === null) return face;
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      {face}
      <ChannelBadge channel={channel} />
    </span>
  );
}
